import React, {useMemo} from "react";
import useIndustry from "../../../../../../../hooks/use-industry";
import {DashboardFilterParameter} from "../../types";
import Grid2 from "@mui/material/Unstable_Grid2";
import {Box, Fade, FormControlLabel, InputAdornment, MenuItem, SvgIcon, Typography, Unstable_Grid2 as Grid} from "@mui/material";
import {Switch} from "../../../../../../../components/base/switch";
import IconButton from "../../../../../../../components/base/button/icon_button";
import classnames from "classnames";
import Collapse from "@mui/material/Collapse";
import {DateRangeButton, PredefinedDateRanges} from "../../../../../../../components/app-specific/date-range-controller";
import {Close, CopyAllRounded} from "@mui/icons-material";
import CopyToClipboard from "../../../../../../../components/base/copy-to-clipboard";
import CategoryAutocomplete from "../../../../../../../components/text-fields/autocomplete/category-autocomplete";
import WarehouseV2Autocomplete from "../../../../../../../components/text-fields/autocomplete/warehouse-v2-autocomplete";
import OperatorAutocomplete from "../../../../../../../components/text-fields/autocomplete/operator-autocomplete";
import PartSupportTypeAutocomplete from "../../../../../../../components/text-fields/autocomplete/part-support-type-autocomplete";
import PartSupportStatusAutocomplete from "../../../../../../../components/text-fields/autocomplete/part-support-status-autocomplete";
import PartSupportReasonAutocomplete from "../../../../../../../components/text-fields/autocomplete/part-support-reason-autocomplete";
import PartSupportVendorStatusAutocomplete from "../../../../../../../components/text-fields/autocomplete/part-support-vendor-status-autocomplete";
import {FinalForm, MuiRFF, ReactFinalForm} from "../../../../../../../components/form";
import {FilterSectionProps, required, Schema} from "./index";
// @ts-ignore
import {ReactComponent as ToggleIcon} from "../../../../../../../../assets/images/warranty-statuses-reasons/toggle.svg";
import ProvinceAutocomplete from "../../../../../../../components/text-fields/autocomplete/provinces-autocomplete";
import SalesPersonAutocomplete from "../../../../../../../components/text-fields/autocomplete/sales-person-autocomplete";
import TerritoryAutocomplete from "../../../../../../../components/text-fields/autocomplete/territory-autocomplete";
import ExternalVendorAutocomplete from "../../../../../../../components/text-fields/autocomplete/vendor-autocomplete/external-vendor";
import ExternalPartAutocomplete from "../../../../../../../components/text-fields/autocomplete/part-autocomplete/external-part";
import ExternalCustomerAutocomplete from "../../../../../../../components/text-fields/autocomplete/customer-autocomplete/external-customer";
import ArTransactionTypesAutocomplete from "../../../../../../../components/text-fields/autocomplete/ar-transaction-types-autocomplete";
import EnvService from "../../../../../../../../core/services/env";
import {
    generateBusinessName,
    generateCategoryName,
    generatePartNumber,
    generatePersonName,
    generateReasonCode,
    generateReasonTitle,
    generateVendorCode,
    generateWarehouseName
} from "../../../../../../../../core/services/utils/name-generator";

export interface DashboardViewFilterSectionContentProps extends ReactFinalForm.FormRenderProps<Schema, Partial<Schema>>, FilterSectionProps {
    collapseIn: boolean;
    setCollapseIn: React.Dispatch<React.SetStateAction<boolean>>;
    hasValues: (values: Schema) => boolean;
    activeValues: [keyof Schema, any][];
    forcedFilterFieldKeys?: Array<keyof Schema>;
}

const DashboardViewFilterSectionContent: React.FC<DashboardViewFilterSectionContentProps> = ({
                                                                                                 values,
                                                                                                 form,
                                                                                                 errors,
                                                                                                 collapseIn,
                                                                                                 setCollapseIn,
                                                                                                 disabled,
                                                                                                 parameters,
                                                                                                 allowMergingWithParentFilters,
                                                                                                 hasValues: hasValuesCallback,
                                                                                                 activeValues,
                                                                                                 viewOnly,
                                                                                                 forcedFilterFieldKeys,
                                                                                                 forcedFilterRequirements,
                                                                                             }) => {
    const {industries, fetchIndustry} = useIndustry({noCategories: true});

    const hasValues = useMemo(() => hasValuesCallback(values), [hasValuesCallback, values]);
    const hasForcedFilters = (forcedFilterFieldKeys?.length ?? 0) > 0;

    const showAvailableFilters = useMemo(() => collapseIn, [collapseIn]);
    const activeValueKeys = useMemo(() => {
        const keys = new Set<keyof Schema>(activeValues.map(([paramKey]) => paramKey));
        for (const key of forcedFilterFieldKeys ?? []) {
            keys.add(key);
        }
        return Array.from(keys);
    }, [activeValues, forcedFilterFieldKeys]);
    const fieldsDisabled = Boolean(disabled || viewOnly);

    if (!parameters.length) {
        return null;
    }

    return (
        <Grid2 container spacing={2}>
            <Grid2 xs={12}>
                <Box display={"flex"} alignItems={"center"} justifyContent={"space-between"}>

                    <Box display={"flex"} alignItems={"center"}>
                        <Typography variant="subtitle1" fontWeight={600}>
                            Dashboard Filters
                        </Typography>
                        {
                            activeValues?.length > 0 &&
                            <Box sx={{
                                backgroundColor: 'warning.main',
                                width: 'fit-content',
                                marginInlineStart: 1.5,
                                paddingBlock: 0.25,
                                paddingInline: 1,
                                borderRadius: 1,
                            }}>
                                <Typography component={'span'} variant="subtitle2" color="warning.contrastText" fontWeight={600}>
                                    {`Active Filters: ${activeValues?.length ?? 0}`}
                                </Typography>
                            </Box>
                        }
                    </Box>
                    {
                        allowMergingWithParentFilters &&
                        <FormControlLabel
                            label="Merge with Dashboard Filters"
                            disabled={fieldsDisabled}
                            sx={{margin: 0, ...(viewOnly && {marginInlineStart: 'auto'})}}
                            control={
                                <Switch
                                    color={'primary'}
                                    className={'mr-2'}
                                    checked={values.mergeWithParentFilters ?? false}
                                    disabled={fieldsDisabled}
                                    onChange={(checked) => form.change('mergeWithParentFilters', checked)}
                                />
                            }
                        />
                    }
                </Box>
            </Grid2>
            {
                (hasValues || hasForcedFilters) &&
                <Grid2 xs={12}>
                    <Typography variant="body2" color="tertiary.main" fontWeight={600}>
                        Active Filters:
                    </Typography>
                </Grid2>
            }
            <Grid2 xs={12}>
                <Filters
                    allowMergingWithParentFilters={allowMergingWithParentFilters}
                    parameters={parameters}
                    values={values}
                    disabled={fieldsDisabled}
                    form={form}
                    errors={errors}
                    industries={industries}
                    fetchIndustry={fetchIndustry as any}
                    activatedValues
                    activeValueKeys={activeValueKeys}
                    forcedFilterRequirements={forcedFilterRequirements}
                />
            </Grid2>
            {
                !viewOnly &&
                <>
                    <Grid2 xs={12}>
                        <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'flex-start', gap: 1}}>
                            <Typography variant="body2" color="tertiary.main" fontWeight={600}>
                                Available Filters:
                            </Typography>
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    paddingBlock: 0.25,
                                    paddingInline: 1,
                                    backgroundColor: 'background.default',
                                    borderRadius: 1,
                                    cursor: 'pointer'
                                }}
                                onClick={() => setCollapseIn(prevState => !prevState)}
                            >
                                <IconButton className={'toggle-button'}>
                                    <SvgIcon component={ToggleIcon} className={classnames('icon small toggle-icon', {'open': collapseIn})}/>
                                </IconButton>
                                <Typography variant="caption" color="secondary.main" fontWeight={600}>
                                    {collapseIn ? ' Hide' : ' Show'}
                                </Typography>
                            </Box>
                        </Box>
                    </Grid2>
                    <Grid2 xs={12}>
                        <Collapse in={showAvailableFilters} timeout={'auto'}>
                            <Fade in={showAvailableFilters} timeout={500}>
                                <Box component={'span'} sx={{width: '100%'}}>
                                    <Filters
                                        allowMergingWithParentFilters={allowMergingWithParentFilters}
                                        parameters={parameters}
                                        values={values}
                                        disabled={fieldsDisabled}
                                        form={form}
                                        errors={errors}
                                        industries={industries}
                                        fetchIndustry={fetchIndustry as any}
                                        activatedValues={false}
                                        activeValueKeys={activeValueKeys}
                                        forcedFilterRequirements={forcedFilterRequirements}
                                    />
                                </Box>
                            </Fade>
                        </Collapse>
                    </Grid2>
                </>
            }
        </Grid2>
    );
}

export default DashboardViewFilterSectionContent;

interface FiltersProps {
    allowMergingWithParentFilters?: boolean;
    parameters: Array<DashboardFilterParameter>;
    values: Partial<Schema>;
    disabled?: boolean;
    form: FinalForm.FormApi<Schema, Partial<Schema>>;
    errors: FinalForm.ValidationErrors;
    industries: any[];
    fetchIndustry: ((params: { industryId?: string, categoryId?: number }, cache?: boolean) => Promise<void | any>);
    activeValueKeys?: (keyof Schema)[];
    activatedValues?: boolean;
    forcedFilterRequirements?: FilterSectionProps['forcedFilterRequirements'];
}

const Filters: React.FC<FiltersProps> = ({parameters, values, disabled, form, industries, fetchIndustry, activeValueKeys, activatedValues, allowMergingWithParentFilters, forcedFilterRequirements}) => {

    /**
     * Transform industry data into option format for select input
     */
    const industryOptions = useMemo(() => (industries as any[]).map(industry => ({
        value: industry.id,
        label: industry.title
    })), [industries]);


    const dateRangeActive = activeValueKeys?.some(v => ['dateRangeValue'].includes(v));
    const currencyActive = activeValueKeys?.some(v => ['currency'].includes(v));
    const partNumbersActive = activeValueKeys?.some(v => ['partNumbers'].includes(v));
    const partNumberPrefixActive = activeValueKeys?.some(v => ['partNumberPrefix'].includes(v));
    const customersActive = activeValueKeys?.some(v => ['customers'].includes(v));
    const territoryCodesActive = activeValueKeys?.some(v => ['territoryCodes'].includes(v));
    const categoriesActive = activeValueKeys?.some(v => ['categories'].includes(v));
    const excludePartNumbersActive = activeValueKeys?.some(v => ['excludePartNumbers'].includes(v));
    const excludeCustomersActive = activeValueKeys?.some(v => ['excludeCustomers'].includes(v));
    const partSupportReasonsActive = activeValueKeys?.some(v => ['partSupportReasons'].includes(v));
    const partSupportStatusesActive = activeValueKeys?.some(v => ['partSupportStatuses'].includes(v));
    const partSupportTypesActive = activeValueKeys?.some(v => ['partSupportTypes'].includes(v));
    const partSupportVendorStatusesActive = activeValueKeys?.some(v => ['partSupportVendorStatuses'].includes(v));
    const warehousesActive = activeValueKeys?.some(v => ['warehouses'].includes(v));
    const salesWarehousesActive = activeValueKeys?.some(v => ['salesWarehouses'].includes(v));
    const operatorsActive = activeValueKeys?.some(v => ['operators'].includes(v));
    const partSupportExcludeReasonsActive = activeValueKeys?.some(v => ['partSupportExcludeReasons'].includes(v));
    const hasCreditedActive = activeValueKeys?.some(v => ['hasCredited'].includes(v));
    const provincesActive = activeValueKeys?.some(v => ['provinces'].includes(v));
    const citiesActive = activeValueKeys?.some(v => ['cities'].includes(v));
    const accountManagersActive = activeValueKeys?.some(v => ['accountManagers'].includes(v));
    const arTransactionTypesActive = activeValueKeys?.some(v => ['arTransactionTypes'].includes(v));
    const vendorNumbersActive = activeValueKeys?.some(v => ['vendorNumbers'].includes(v));
    // TODO: add more filters

    const categoriesRequirement = forcedFilterRequirements?.find((requirement) => requirement.filterName === DashboardFilterParameter.PartCategories);
    const categoriesMaxCount = categoriesRequirement?.requiredMaxCount;
    const categoriesRequired = Boolean(categoriesRequirement?.isRequired);

    const showDateRange = (activatedValues === dateRangeActive) && (parameters.includes(DashboardFilterParameter.StartDate) || parameters.includes(DashboardFilterParameter.EndDate));
    const showCurrency = (activatedValues === currencyActive) && parameters.includes(DashboardFilterParameter.CurrencyCode) && !!values.currency;
    const showPartNumbers = (activatedValues === partNumbersActive) && parameters.includes(DashboardFilterParameter.PartNumbers);
    const showPartNumberPrefix = (activatedValues === partNumberPrefixActive) && parameters.includes(DashboardFilterParameter.PartNumberPrefix);
    const showCustomers = (activatedValues === customersActive) && parameters.includes(DashboardFilterParameter.CustomerIds);
    const showTerritoryCodes = (activatedValues === territoryCodesActive) && parameters.includes(DashboardFilterParameter.TerritoryCodes);
    const showCategories = (activatedValues === categoriesActive) && parameters.includes(DashboardFilterParameter.PartCategories) && !!values.industry;
    const showIndustry = parameters.includes(DashboardFilterParameter.Industry) && (
        showCategories || (Boolean(categoriesRequirement) && activatedValues === true)
    );
    const showExcludePartNumbers = (activatedValues === excludePartNumbersActive) && parameters.includes(DashboardFilterParameter.ExcludePartNumbers);
    const showExcludeCustomers = (activatedValues === excludeCustomersActive) && parameters.includes(DashboardFilterParameter.ExcludeCustomerIds);
    const showPartSupportReasons = (activatedValues === partSupportReasonsActive) && parameters.includes(DashboardFilterParameter.PSR_ReasonIds);
    const showPartSupportStatuses = (activatedValues === partSupportStatusesActive) && parameters.includes(DashboardFilterParameter.PSR_StatusIds);
    const showPartSupportTypes = (activatedValues === partSupportTypesActive) && parameters.includes(DashboardFilterParameter.PSR_Types);
    const showPartSupportVendorStatuses = (activatedValues === partSupportVendorStatusesActive) && parameters.includes(DashboardFilterParameter.PSR_VendorStatus);
    const showWarehouses = (activatedValues === warehousesActive) && parameters.includes(DashboardFilterParameter.WarehouseCodes);
    const showSalesWarehouses = (activatedValues === salesWarehousesActive) && parameters.includes(DashboardFilterParameter.SalesWarehouseCodes);
    const showOperators = (activatedValues === operatorsActive) && parameters.includes(DashboardFilterParameter.OperatorIds);
    const showPartSupportExcludeReasons = (activatedValues === partSupportExcludeReasonsActive) && parameters.includes(DashboardFilterParameter.PSR_ExcludeReasonIds);
    const showHasCredited = (activatedValues === hasCreditedActive) && parameters.includes(DashboardFilterParameter.HasCredited);
    const showProvinces = (activatedValues === provincesActive) && parameters.includes(DashboardFilterParameter.ProvinceCodes);
    const showCities = (activatedValues === citiesActive) && parameters.includes(DashboardFilterParameter.Cities);
    const showAccountManagers = (activatedValues === accountManagersActive) && parameters.includes(DashboardFilterParameter.AccountManagersCodes);
    const showArTransactionTypes = (activatedValues === arTransactionTypesActive) && parameters.includes(DashboardFilterParameter.ArTransactionTypes);
    const showVendorNumbers = (activatedValues === vendorNumbersActive) && parameters.includes(DashboardFilterParameter.VendorNumbers);
    // TODO: add more filters

    return (
        <Grid2 container spacing={2} sx={{alignItems: 'center'}}>
            {/* Date Range Pickers */}
            {
                showDateRange &&
                <Grid xs={12} sx={{display: 'flex', justifyContent: 'start', alignItems: 'center', gap: 1}}>
                    <DateRangeButton
                        popoverProps={allowMergingWithParentFilters ? {
                            anchorOrigin: {vertical: 'top', horizontal: 'right'},
                            transformOrigin: {vertical: 'center', horizontal: 'left'},
                        } : {}}
                        predefinedOptions={PredefinedDateRanges.accountingRanges}
                        disabled={disabled}
                        disallowedTypes={['next']}
                        disallowedBatches={['hour']}
                        disallowFromNow
                        value={values.dateRangeValue}
                        onCommitted={(value) => {
                            form.change('dateRangeValue', value);
                            setTimeout(() => void form.submit(), 100);
                        }}
                    />
                </Grid>
            }
            <Grid2 xs={12} sx={{padding: 0, margin: 0, height: 0}}/>
            {
                showCurrency &&
                <Grid2 xs={12} sm={6} md={4}>
                    <MuiRFF.TextField
                        name="currency"
                        label="Currency Code"
                        fullWidth
                        size={'small'}
                        required={required.currency}
                        disabled={disabled}
                        InputProps={{
                            readOnly: true,
                            endAdornment:
                                <InputAdornment position="end">
                                    <IconButton color={'default'} onClick={() => form.change('currency', undefined)} size="small">
                                        <Close/>
                                    </IconButton>
                                </InputAdornment>
                        }}
                    />
                </Grid2>
            }
            {
                showPartNumbers &&
                <Grid2 xs={12}>
                    <ExternalPartAutocomplete<true>
                        multiple
                        name="partNumbers"
                        label="Parts"
                        placeholder="Search for part..."
                        fullWidth
                        form
                        disabled={disabled}
                        textFieldProps={{size: 'small'}}
                        {...EnvService.enableDemoNameMasking && {getOptionLabel: (option) => generatePartNumber(option.partNo)}}

                    />
                </Grid2>
            }
            {
                showPartNumberPrefix &&
                <Grid2 xs={12}>
                    <MuiRFF.TextField
                        name="partNumberPrefix"
                        label="Part Number Prefix"
                        fullWidth
                        disabled={disabled}
                        size={'small'}
                    />
                </Grid2>
            }
            {
                showCustomers &&
                <Grid2 xs={12}>
                    <ExternalCustomerAutocomplete<true>
                        multiple
                        name="customers"
                        label="Customers"
                        placeholder="Search for customer..."
                        fullWidth
                        form
                        disabled={disabled}
                        textFieldProps={{size: 'small'}}
                        {...EnvService.enableDemoNameMasking && {getOptionLabel: (option) => generateBusinessName(option.name)}}
                    />
                </Grid2>
            }
            {
                showTerritoryCodes &&
                <Grid2 xs={12}>
                    <TerritoryAutocomplete<string, true>
                        form
                        multiple
                        name="territoryCodes"
                        label="Territory Codes"
                        placeholder="Search for territory..."
                        fullWidth
                        disabled={disabled}
                        textFieldProps={{
                            size: 'small'
                        }}
                        transformRecord={(option) => option.code ?? ""}
                        isOptionEqualToValue={(option, value) => option === value}
                        getOptionLabel={(option) => option}
                        {...EnvService.enableDemoNameMasking && {getOptionLabel: (option) => generateVendorCode(option)}}
                        getOptionKey={(option) => option}
                    />
                </Grid2>
            }
            {
                showVendorNumbers &&
                <Grid2 xs={12}>
                    <ExternalVendorAutocomplete<string, true>
                        multiple
                        form
                        name="vendorNumbers"
                        label="Vendor Numbers"
                        placeholder="Search for vendor..."
                        fullWidth
                        disabled={disabled}
                        textFieldProps={{size: 'small'}}
                        transformRecord={(option) => option.code ?? ""}
                        isOptionEqualToValue={(option, value) => option === value}
                        getOptionLabel={(option) => option}
                        {...EnvService.enableDemoNameMasking && {getOptionLabel: (option) => generateVendorCode(option)}}
                        getOptionKey={(option) => option}
                    />
                </Grid2>
            }
            {
                showProvinces &&
                <Grid2 xs={12}>
                    <ProvinceAutocomplete<true>
                        multiple
                        name="provinces"
                        label="Provinces"
                        placeholder="Search for province..."
                        fullWidth
                        disabled={disabled}
                        form
                        textFieldProps={{size: 'small'}}
                    />
                </Grid2>
            }
            {
                showCities &&
                <Grid2 xs={12}>
                    <MuiRFF.Autocomplete
                        fullWidth
                        options={[]}
                        multiple
                        freeSolo
                        disabled={disabled}
                        name={'cities'}
                        required={required.cities}
                        label={"Cities"}
                        sx={{
                            '.MuiOutlinedInput-root .MuiAutocomplete-endAdornment': {
                                right: '45px',
                            },
                        }}
                        onChange={(e, values) => {
                            // ensure any comma-separated values are split into an array
                            const newValue = values.map((v: string) => v.trim().split(',').map((v: string) => v.trim())).flat();
                            form.change('cities', newValue);
                        }}
                        componentsProps={{
                            clearIndicator: {color: 'default'},
                            popupIndicator: {color: 'default'},
                        }}
                        textFieldProps={{
                            size: 'small',
                            placeholder: 'search for city...',
                            InputProps: {
                                endAdornment:
                                    values.cities?.length > 0
                                        ? <CopyToClipboard tooltipProps={{}}>
                                            {({copy}) =>
                                                <IconButton
                                                    sx={{
                                                        position: 'absolute',
                                                        right: '9px',
                                                        top: '50%',
                                                        transform: 'translate(0, -50%)',
                                                    }}
                                                    size={'small'}
                                                    onPressed={() => copy(values.cities?.join(', ') ?? "")}
                                                    color={'paper.secondary'}
                                                    className={'copy-to-clipboard-button'}
                                                >
                                                    <CopyAllRounded/>
                                                </IconButton>
                                            }
                                        </CopyToClipboard>
                                        : null
                            }
                        }}
                    />
                </Grid2>
            }
            {
                showAccountManagers &&
                <Grid2 xs={12}>
                    <SalesPersonAutocomplete<true>
                        multiple
                        name="accountManagers"
                        label="Account Managers"
                        placeholder="Search for account managers..."
                        fullWidth
                        disabled={disabled}
                        form
                        textFieldProps={{size: 'small'}}
                        {...EnvService.enableDemoNameMasking && {getOptionLabel: (option) => generatePersonName(option.name ?? "")}}
                    />
                </Grid2>
            }
            <Grid2 xs={12} sx={{padding: 0, margin: 0, height: 0}}/>
            {/* Industry Select */}
            {
                showIndustry &&
                <Grid2 xs={12} sm={6} md={4}>
                    <MuiRFF.TextField
                        name="industry"
                        label="Industry"
                        select
                        fullWidth
                        disabled={disabled}
                        size={'small'}
                        SelectProps={{
                            onChange: (e: any) => {
                                void fetchIndustry({industryId: e.target.value}, false)
                                form.change('industry', e.target.value);
                                form.change('categories', []);
                            },
                            renderValue: (value) => (industryOptions.find((i) => i.value === value) as any)?.label,
                        }}
                    >
                        {industryOptions.map((option) => (
                            <MenuItem key={option.value} value={option.value}>
                                {option.label}
                            </MenuItem>
                        ))}
                    </MuiRFF.TextField>
                </Grid2>
            }
            {
                showCategories &&
                <Grid2 xs={12} sm={6} md={8}>
                    <CategoryAutocomplete<true>
                        multiple
                        onlyRoot
                        disabled={disabled}
                        industryId={values?.industry}
                        name="categories"
                        label="Categories"
                        placeholder="Search for category..."
                        fullWidth
                        form
                        required={categoriesRequired}
                        getOptionDisabled={(option) => {
                            if (categoriesMaxCount == null) {
                                return false;
                            }
                            const selected = values.categories ?? [];
                            const isSelected = selected.some((category: {id?: number}) => category?.id === option.id);
                            return !isSelected && selected.length >= categoriesMaxCount;
                        }}
                        textFieldProps={{size: 'small'}}
                        {...EnvService.enableDemoNameMasking && {getOptionLabel: (option) => generateCategoryName(option.title),}}
                    />
                </Grid2>
            }
            {
                showExcludePartNumbers &&
                <Grid2 xs={12}>
                    <ExternalPartAutocomplete<true>
                        multiple
                        name="excludePartNumbers"
                        label="Excluded Parts"
                        placeholder="Search for part..."
                        fullWidth
                        form
                        disabled={disabled}
                        textFieldProps={{size: 'small'}}
                        {...EnvService.enableDemoNameMasking && {getOptionLabel: (option) => generatePartNumber(option.partNo)}}
                    />
                </Grid2>
            }
            {
                showExcludeCustomers &&
                <Grid2 xs={12}>
                    <ExternalCustomerAutocomplete<true>
                        multiple
                        name="excludeCustomers"
                        label="Excluded Customers"
                        placeholder="Search for customer..."
                        fullWidth
                        disabled={disabled}
                        form
                        textFieldProps={{size: 'small'}}
                        {...EnvService.enableDemoNameMasking && {getOptionLabel: (option) => generateBusinessName(option.name)}}
                    />
                </Grid2>
            }
            {
                showWarehouses &&
                <Grid2 xs={12}>
                    <WarehouseV2Autocomplete<true>
                        multiple
                        name="warehouses"
                        label="Warehouses"
                        placeholder="Search for warehouse..."
                        fullWidth
                        disabled={disabled}
                        form
                        textFieldProps={{size: 'small'}}
                        {...EnvService.enableDemoNameMasking && {getOptionLabel: (option) => generateWarehouseName(option.name ?? option.title ?? "")}}
                    />
                </Grid2>
            }
            {
                showSalesWarehouses &&
                <Grid2 xs={12}>
                    <WarehouseV2Autocomplete<true>
                        multiple
                        name="salesWarehouses"
                        label="Sales Warehouses"
                        placeholder="Search for warehouse..."
                        fullWidth
                        disabled={disabled}
                        form
                        textFieldProps={{size: 'small'}}
                        {...EnvService.enableDemoNameMasking && {getOptionLabel: (option) => generateWarehouseName(option.name ?? option.title ?? "")}}
                    />
                </Grid2>
            }
            {
                showOperators &&
                <Grid2 xs={12}>
                    <OperatorAutocomplete<true>
                        multiple
                        name="operators"
                        label="Operators"
                        placeholder="Search for operator..."
                        fullWidth
                        disabled={disabled}
                        form
                        textFieldProps={{size: 'small'}}
                        {...EnvService.enableDemoNameMasking && {getOptionLabel: (option) => generatePersonName([option.firstName ?? "", option.lastName ?? ""].filter(Boolean).join(" "))}}
                    />
                </Grid2>
            }
            {
                showPartSupportTypes &&
                <Grid2 xs={12}>
                    <PartSupportTypeAutocomplete<true>
                        multiple
                        name="partSupportTypes"
                        label="Part Support Types"
                        placeholder="Search for part support type..."
                        fullWidth
                        disabled={disabled}
                        form
                        textFieldProps={{size: 'small'}}
                    />
                </Grid2>
            }
            {
                showArTransactionTypes &&
                <Grid2 xs={12}>
                    <ArTransactionTypesAutocomplete<true>
                        multiple
                        name="arTransactionTypes"
                        label="AR Transaction Types"
                        placeholder="Search for AR transaction type..."
                        fullWidth
                        disabled={disabled}
                        form
                        textFieldProps={{size: 'small'}}
                    />
                </Grid2>
            }
            {
                showPartSupportStatuses &&
                <Grid2 xs={12}>
                    <PartSupportStatusAutocomplete<true>
                        multiple
                        name="partSupportStatuses"
                        label="Part Support Statuses"
                        placeholder="Search for part support status..."
                        fullWidth
                        disabled={disabled}
                        form
                        onChange={(_, value) => {
                            const values = Array.isArray(value) ? value : !!value ? [value] : [];
                            form.change('partSupportStatuses', values);
                            form.change('partSupportReasons', []);
                        }}
                        textFieldProps={{size: 'small'}}
                        {...EnvService.enableDemoNameMasking && {getOptionLabel: (option) => generateVendorCode(option.title ?? "")}}
                    />
                </Grid2>
            }
            {
                showPartSupportReasons &&
                <Grid2 xs={12}>
                    <PartSupportReasonAutocomplete<true>
                        multiple
                        name="partSupportReasons"
                        label="Part Support Reasons"
                        placeholder="Search for part support reason..."
                        fullWidth
                        disabled={disabled}
                        form
                        textFieldProps={{size: 'small'}}
                        {...EnvService.enableDemoNameMasking && {getOptionLabel: (option) => `${generateReasonCode(option.code ?? "")} - ${generateReasonTitle(option.title ?? "")}`}}
                    />
                </Grid2>
            }
            {
                showPartSupportExcludeReasons &&
                <Grid2 xs={12}>
                    <PartSupportReasonAutocomplete<true>
                        multiple
                        name="partSupportExcludeReasons"
                        label="Part Support Excluded Reasons"
                        placeholder="Search for part support reason..."
                        fullWidth
                        disabled={disabled}
                        form
                        textFieldProps={{
                            size: 'small'
                        }}
                    />
                </Grid2>
            }
            {
                showPartSupportVendorStatuses &&
                <Grid2 xs={12}>
                    <PartSupportVendorStatusAutocomplete<true>
                        multiple
                        name="partSupportVendorStatuses"
                        label="Part Support Vendor Statuses"
                        placeholder="Search for part support vendor status..."
                        fullWidth
                        disabled={disabled}
                        form
                        textFieldProps={{
                            size: 'small'
                        }}
                    />
                </Grid2>
            }
            {
                showHasCredited &&
                <Grid2 xs={12}>
                    <MuiRFF.Radios
                        name={"hasCredited"}
                        label={"Has Credited"}
                        disabled={disabled}
                        size={'small'}
                        radioGroupProps={{
                            sx: {flexDirection: 'row', gap: 2},
                        }}
                        formLabelProps={{
                            sx: {fontSize: 'small', fontWeight: 600, display: 'flex', alignItems: 'center', marginInlineEnd: 2, lineHeight: 'initial'},
                        }}
                        formControlProps={{
                            sx: {display: 'flex', flexDirection: 'row', flexWrap: 'wrap'},
                        }}
                        formHelperTextProps={{
                            sx: {width: '100%', margin: 0},
                        }}
                        data={[
                            {label: 'Credited Only', value: 'true',},
                            {label: 'Not Credited Only', value: 'false'},
                            {label: 'Either', value: 'null'}
                        ]}
                    />
                </Grid2>
            }
        </Grid2>
    );
}


